'use server';
import { actionErrorMessage } from '@/shared/infrastructure/logging/actionError';
import { screenGenerationPrompt } from '@/shared/infrastructure/content-safety/contentSafety';

import type { ActionResult } from '@/shared/types/actions';
import { SessionService } from '@/contexts/authentication';
import type { CreateCharacterInput, Character } from '../types';
import { CharacterCreationMethod } from '../types';

export async function createCharacter(
  input: CreateCharacterInput,
): Promise<ActionResult<Character>> {
  try {
    const familyId = await SessionService.getFamilyId();
    const userId = await SessionService.getUserId();
    if (!familyId || !userId) return { success: false, error: 'Not authenticated' };

    // Pre-generation IP/trademark content-safety screen (good-faith guardrail).
    const screen = await screenGenerationPrompt({
      text: [input.name, input.physicalDescription, input.personalityTraits, input.specialAbilities]
        .filter(Boolean)
        .join('\n'),
      context: 'character',
      identityId: userId,
    });
    if (!screen.allowed) return { success: false, error: screen.userMessage };

    const { CharacterService } = await import('@/contexts/characters/services/CharacterService');
    const character = await CharacterService.createCharacter({
      familyId,
      creatorId: userId,
      name: input.name,
      role: input.role ?? input.typicalRole,
      gender: input.gender ?? undefined,
      age: input.age ?? undefined,
      physicalDescription: input.physicalDescription || '',
      personalityTraits: input.personalityTraits || '',
      specialAbilities: input.specialAbilities ?? undefined,
      synonyms: input.synonyms ?? [],
      creationMethod: CharacterCreationMethod.Manual,
    });

    return { success: true, data: character as Character };
  } catch (e: unknown) {
    return { success: false, error: actionErrorMessage('createCharacter', e) };
  }
}
